import React, { ComponentType } from 'react';
import RNToast, { ToastConfigParams } from 'react-native-toast-message';
import { IconProps } from '../../icons/IconBase';
import {
  IconToastError,
  IconToastWarning,
  IconToastSuccess,
  IconToastInfo,
  IconToastFeature,
} from '../../icons';
import ToastIcon from './ToastIcon';
import ToastText from './ToastText';
import ToastCloseButton from './ToastCloseButton';
import ToastActionButton from './ToastActionButton';
import styles from './Toast.module.scss';

export type ToastType = 'success' | 'feature' | 'error' | 'info' | 'warning';
export type ToastVariant = 'solid' | 'light' | 'outline';
export type ToastSize = 'x-small' | 'small' | 'large';
export interface ToastProps {
  type: ToastType;
  variant?: ToastVariant;
  size?: ToastSize;
  title?: string;
  text?: string;
  icon?: ComponentType<IconProps>;
  actionText?: string;
  onAction?: () => void;
  autoHide?: boolean;
  visibilityTime?: number;
  position?: 'top' | 'bottom';
}

const defaultIcon: Record<ToastType, ComponentType<IconProps>> = {
  success: IconToastSuccess,
  feature: IconToastFeature,
  error: IconToastError,
  info: IconToastInfo,
  warning: IconToastWarning,
};

export const showToast = ({
  title,
  text,
  type,
  variant = 'light',
  size = 'small',
  icon,
  actionText,
  onAction,
  autoHide = true,
  visibilityTime,
  position,
}: ToastProps) => {
  let displayTitle = title;
  let displayText = text;

  // For small/x-small toasts, combine title and text since text2 isn't rendered
  if (size === 'small' || size === 'x-small') {
    if (text && !title) {
      displayTitle = text;
      displayText = undefined;
    } else if (text && title) {
      // If both are provided, prefer text (the actual message) over generic titles
      // Common pattern: title='Success!', text='Actual message'
      const genericTitles = [
        'Success!',
        'Error',
        'Info',
        'Warning',
        'Updated',
        'Success',
      ];
      if (genericTitles.includes(title)) {
        displayTitle = text;
        displayText = undefined;
      } else {
        displayTitle = title;
        displayText = undefined;
      }
    }
  }

  return RNToast.show({
    type,
    text1: displayTitle,
    text2: displayText,
    autoHide,
    visibilityTime,
    ...(position ? { position } : {}),
    swipeable: false,
    props: {
      icon,
      variant,
      size,
      actionText,
      onAction,
    },
  });
};

const sizeConfig = {
  'x-small': {
    iconSize: 16,
    fontSize: '12px',
    lineHeight: '16px',
    letterSpacing: 'normal',
    fontWeight: '400',
  },
  small: {
    iconSize: 20,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '-0.006em',
    fontWeight: '400',
  },
  large: {
    iconSize: 20,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '-0.006em',
    fontWeight: '500',
  },
} as const;

const Toast = ({
  type,
  text1,
  text2,
  props,
}: ToastConfigParams<{
  icon?: ComponentType<IconProps>;
  variant?: ToastVariant;
  size?: ToastSize;
  actionText?: string;
  onAction?: () => void;
}>) => {
  const toastType = type as ToastType;
  const variant = props.variant || 'outline';
  const size = props.size || 'small';
  const Icon = props.icon ?? defaultIcon[toastType];
  const hasDescription = Boolean(text2);
  const config = sizeConfig[size];

  const getToastStyles = () => {
    const baseClass = styles.toast;
    const sizeClass = size === 'x-small' ? styles.xSmall : styles[size];
    const variantClass = styles[variant];
    const typeClass =
      styles[
        `${toastType}${variant.charAt(0).toUpperCase() + variant.slice(1)}`
      ];
    return `${baseClass} ${sizeClass} ${variantClass} ${typeClass}`;
  };

  const hasAction = props.actionText && props.onAction;

  // Large layout (multi-row with description and actions)
  if (size === 'large') {
    return (
      <div className={getToastStyles()}>
        <div className={styles.toastContentLarge}>
          <ToastIcon
            icon={Icon}
            size={config.iconSize}
            className={styles.toastIconLarge}
          />
          <div className={styles.toastBodyLarge}>
            <div className={styles.toastTextLarge}>
              <ToastText
                text={text1}
                fontSize={config.fontSize}
                lineHeight={config.lineHeight}
                letterSpacing={config.letterSpacing}
                fontWeight={config.fontWeight}
              />
              {hasDescription && (
                <ToastText
                  text={text2}
                  fontSize={config.fontSize}
                  lineHeight={config.lineHeight}
                  letterSpacing={config.letterSpacing}
                  fontWeight={config.fontWeight}
                  isDescription
                />
              )}
            </div>
            {hasAction && (
              <div className={styles.toastActionsLarge}>
                <ToastActionButton
                  actionText={props.actionText}
                  onAction={props.onAction}
                  className={styles.actionButtonLarge}
                />
              </div>
            )}
          </div>
          <ToastCloseButton
            size={config.iconSize}
            className={styles.closeButtonLarge}
          />
        </div>
      </div>
    );
  }

  // X-small and Small layouts (single row)
  return (
    <div className={getToastStyles()}>
      <div className={styles.toastContent}>
        <ToastIcon
          icon={Icon}
          size={config.iconSize}
          className={styles.toastIcon}
        />
        <div className={styles.toastText}>
          <ToastText
            text={text1}
            fontSize={config.fontSize}
            lineHeight={config.lineHeight}
            letterSpacing={config.letterSpacing}
            fontWeight={config.fontWeight}
          />
        </div>
        {hasAction && (
          <ToastActionButton
            actionText={props.actionText}
            onAction={props.onAction}
            className={styles.actionButton}
          />
        )}
        <ToastCloseButton
          size={config.iconSize}
          className={styles.closeButton}
        />
      </div>
    </div>
  );
};

export default Toast;
